import { Filter, Loader2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  EntityTypeFilterPanel,
  type EntityTypeStat,
} from '@/components/entity-filter/EntityTypeFilterPanel'
import type { EmptyStateInsightsPromptProps } from '@/components/investigations/EmptyStateInsightsPrompt'
import { EmptyStateInsightsPrompt } from '@/components/investigations/EmptyStateInsightsPrompt'
import { ObjectSetChipsBar } from '@/components/investigations/ObjectSetChipsBar'
import { useAgentSidebar } from '@/components/layout/AgentSidebarContext'
import { LABEL_COLORS } from '@/components/neo4j/cytoscape/entity-icons'
import type { SelectedEdge, SelectedNode } from '@/components/neo4j/cytoscape/types'
import { EntityHoverPopup } from '@/components/neo4j/shared/EntityHoverPopup'
import { buildDensity, TimeRangeFilter } from '@/components/time-range-filter'
import { SignalLostOverlay } from '@/components/timeline/SignalLostOverlay'
import { Card } from '@/components/ui/card'
import { useObjectSet } from '@/features/investigations/hooks/useObjectSet'
import { snippetSelection } from '@/features/source-snippets/object-set'
import { useSnippetSelection } from '@/features/source-snippets/selection'
import { isMobileViewport } from '@/hooks/use-mobile'
import { useInvestigateContext } from '@/pages/cases/InvestigateContext'
import type { TimelineItem, TimelineParticipant } from '@/services/api/timeline'
import { usePureTimelineStore } from '@/stores/pure-timeline-store'
import { useSelectionStore } from '@/stores/selection-store'
import { cn } from '@/utils/cn'
import { glyphForLabels } from './entity-glyphs'
import { TimelineCanvas } from './TimelineCanvas'
import type { TimelineCardEvent, TimelineHoverPayload } from './TimelineEventCard'
import { usePureTimelineData } from './usePureTimelineData'
import { useTimelineViewport } from './useTimelineViewport'

const FALLBACK_LABEL_COLOR = { fill: '#64748b', stroke: '#475569' }
// Right offset of the floating button stack. The open InvestigationSidebar
// sits at `right-4` and is 372px wide, so its left edge is 388px from the
// canvas edge: 400px clears it with a small gap.
const STACK_RIGHT_PX = 12
const STACK_RIGHT_BESIDE_SIDEBAR_PX = 400

/** Resolve the primary entity label for filter grouping.
 *
 *  - Edge-sourced items group under the SOURCE participant's type so a
 *    Person→Account edge appears under "Person" (you'd typically filter
 *    by the actor side). For node-sourced items the primary entity IS
 *    the source.
 *  - Falls back to the raw ``source_type`` then to "Unknown" so every
 *    item still picks up a stat row instead of silently disappearing.
 */
function primaryEntityLabel(it: TimelineItem): string {
  if (it.source_kind === 'edge') {
    const source = it.participants.others.find(p => p.relation === 'source')
    return source?.type ?? it.source_type ?? 'Unknown'
  }
  return it.participants.primary.type ?? it.source_type ?? 'Unknown'
}

interface PureTimelineViewProps {
  caseId: string
  /** Optional: full prop bundle for the empty-state overlay. When omitted,
   *  the timeline falls back to a minimal "ask agent" prompt — used by the
   *  /docs preview where there's no case context. */
  emptyStateProps?: Omit<EmptyStateInsightsPromptProps, 'view'>
  /** Optional: rendered inside the events canvas area (the `relative flex-1`
   *  region). Lets a host page float its own sidebar over the events without
   *  covering the top bar or the bottom time-range minimap, mirroring how the
   *  Geo view positions its InvestigationSidebar. */
  sidebarSlot?: React.ReactNode
  /** Optional: rendered below the canvas filter toggle in the top-right
   *  button stack. Host pages use this to slot e.g. a SidebarOpenButton
   *  without each one re-implementing the absolute positioning. */
  topRightAccessory?: React.ReactNode
  /** Whether the sidebar in `sidebarSlot` is showing. The floating button
   *  stack then moves left to clear it. On a phone the sidebar covers the
   *  whole canvas instead, so the stack and the time-range strip step aside
   *  and leave their room to it. */
  sidebarOpen?: boolean
}

// Build a minimal SelectedNode from a timeline-item participant. We no
// longer carry the full graph in memory (the upfront knowledge-graph
// fetch was dropped to cut transfer cost); the sidebar's entity-detail
// panel lazy-fetches the full property bag from just the id, so the
// payload here only needs to cover the few fields the side surface uses
// before the fetch resolves.
function participantToSelectedNode(p: TimelineParticipant): SelectedNode {
  return {
    id: p.id ?? '',
    name: p.label ?? p.id ?? 'unknown',
    labels: p.type ? [p.type] : [],
    typeLabel: p.type ?? undefined,
    properties: {},
  }
}

function itemToSelectedEdge(item: TimelineItem): SelectedEdge | null {
  if (item.source_kind !== 'edge') return null
  const src = item.participants.others.find(p => p.relation === 'source')
  const tgt = item.participants.others.find(p => p.relation === 'target')
  return {
    id: item.source_id,
    type: item.source_type,
    source: src?.id ?? '',
    target: tgt?.id ?? '',
    sourceName: src?.label ?? undefined,
    targetName: tgt?.label ?? undefined,
    properties: {},
    sourceNode: src
      ? {
          id: src.id ?? '',
          labels: src.type ? [src.type] : [],
          properties: {},
        }
      : undefined,
    targetNode: tgt
      ? {
          id: tgt.id ?? '',
          labels: tgt.type ? [tgt.type] : [],
          properties: {},
        }
      : undefined,
  }
}

function timelineItemToCardEvent(item: TimelineItem): TimelineCardEvent {
  const timestamp = item.kind === 'point' ? item.at : item.start
  const endTimestamp = item.kind === 'interval' ? (item.end ?? undefined) : undefined
  const isInterval = item.kind === 'interval'

  if (item.source_kind === 'edge') {
    const sourcePart = item.participants.others.find(p => p.relation === 'source')
    const targetPart = item.participants.others.find(p => p.relation === 'target')
    const srcId = sourcePart?.id ?? ''
    const tgtId = targetPart?.id ?? ''
    return {
      id: item.id,
      type: item.source_type,
      timestamp,
      endTimestamp,
      isInterval,
      sourceId: srcId,
      sourceName: sourcePart?.label ?? srcId ?? 'unknown',
      sourceGlyph: glyphForLabels(sourcePart?.type ? [sourcePart.type] : []),
      targetId: tgtId || undefined,
      targetName: tgtId ? (targetPart?.label ?? tgtId) : undefined,
      targetGlyph: tgtId ? glyphForLabels(targetPart?.type ? [targetPart.type] : []) : undefined,
    }
  }

  // Node-sourced — single primary participant, no target line.
  const primary = item.participants.primary
  return {
    id: item.id,
    type: item.event_label || item.event_kind,
    timestamp,
    precision:
      item.source_kind === 'snippet' && item.display_props.precision === 'day'
        ? 'day'
        : item.source_kind === 'snippet'
          ? 'instant'
          : undefined,
    endTimestamp,
    isInterval,
    sourceId: primary.id ?? item.source_id,
    sourceName: primary.label ?? item.source_label,
    sourceGlyph: glyphForLabels(primary.type ? [primary.type] : [item.source_type]),
  }
}

export function PureTimelineView({
  caseId,
  emptyStateProps,
  sidebarSlot,
  topRightAccessory,
  sidebarOpen = false,
}: PureTimelineViewProps) {
  const topRightOffsetPx = sidebarOpen ? STACK_RIGHT_BESIDE_SIDEBAR_PX : STACK_RIGHT_PX
  const { t } = useTranslation()
  const { activeGraphFilters } = useInvestigateContext()
  const objectSet = useObjectSet()
  const agent = useAgentSidebar()
  // The hook scopes to the active investigation server-side (passing
  // ``node_ids`` to ``/v1/cases/{id}/timeline/items``). Result already obeys
  // the object-set membership rule — no client-side filter needed here.
  // When no investigation is active or the active one is empty, the hook
  // disables the query and ``items`` is an empty array, naturally driving
  // the empty-state prompt below.
  const {
    items: scopedItems,
    globalRange,
    isLoading,
    isError,
    hasBypassedFilter,
  } = usePureTimelineData(caseId, activeGraphFilters)

  // Local per-view filter: hide events whose primary entity label is in this set.
  // Mirrors the Geo view's hiddenEntityTypes — but timeline doesn't have a global
  // store equivalent yet, so we keep state local to the component.
  const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(new Set())
  const [filterOpen, setFilterOpen] = useState(false)

  const items = useMemo(() => {
    if (hiddenTypes.size === 0) return scopedItems
    return scopedItems.filter(it => !hiddenTypes.has(primaryEntityLabel(it)))
  }, [scopedItems, hiddenTypes])

  // Stats computed from the UNFILTERED set so hidden types stay listed and
  // toggleable in the panel (you can always un-hide).
  const entityTypeStats = useMemo<EntityTypeStat[]>(() => {
    const counts = new Map<string, number>()
    for (const it of scopedItems) {
      const label = primaryEntityLabel(it)
      counts.set(label, (counts.get(label) ?? 0) + 1)
    }
    return Array.from(counts.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([type, count]) => ({
        type,
        count,
        isHidden: hiddenTypes.has(type),
        color: LABEL_COLORS[type] ?? FALLBACK_LABEL_COLOR,
      }))
  }, [scopedItems, hiddenTypes])

  const hiddenCount = hiddenTypes.size
  const toggleEntityType = useCallback((type: string) => {
    setHiddenTypes(prev => {
      const next = new Set(prev)
      if (next.has(type)) next.delete(type)
      else next.add(type)
      return next
    })
  }, [])
  const showAllEntityTypes = useCallback(() => setHiddenTypes(new Set()), [])

  // Look-up tables driven entirely by the timeline-items payload. The
  // ``itemMap`` powers edge-selection (click the card header → resolve the
  // timeline item by deterministic id). The ``participantMap`` provides
  // minimal name/type info for any entity referenced by *any* item, so
  // node clicks and hovers can render a sensible chip before the side
  // panel fetches the full entity details.
  const itemMap = useMemo(() => new Map(items.map(it => [it.id, it])), [items])
  const participantMap = useMemo(() => {
    const m = new Map<string, TimelineParticipant>()
    for (const it of items) {
      const primary = it.participants.primary
      if (primary.id && !m.has(primary.id)) m.set(primary.id, primary)
      for (const p of it.participants.others) {
        if (p.id && !m.has(p.id)) m.set(p.id, p)
      }
    }
    return m
  }, [items])

  const cardEvents = useMemo<TimelineCardEvent[]>(() => items.map(timelineItemToCardEvent), [items])

  const globalStartMs = globalRange ? new Date(globalRange.start).getTime() : null
  const globalEndMs = globalRange ? new Date(globalRange.end).getTime() : null

  // Per-item timestamps drive the smart-fit (P5–P95) used by the viewport's
  // initial window and the "auto-fit" button. Outliers (a single 2038 typo
  // event in a case otherwise concentrated in 2026) no longer hijack the
  // axis — the user can still pan/zoom out to reach them.
  const itemTimestamps = useMemo(() => {
    const out: number[] = []
    for (const it of items) {
      const t = new Date(it.kind === 'point' ? it.at : it.start).getTime()
      if (Number.isFinite(t)) out.push(t)
    }
    return out
  }, [items])

  const { viewport, fitAll, zoomAtCursor, panBy, setViewport } = useTimelineViewport(
    globalStartMs,
    globalEndMs,
    itemTimestamps
  )

  // Density bins for the bottom TimeRangeFilter histogram. Computed from
  // the same itemTimestamps that drove smartFit so the bar shape lines up
  // with where the actual signal lives. Recomputes only when bounds or the
  // raw timestamp list change.
  const timelineDensity = useMemo(() => {
    if (globalStartMs === null || globalEndMs === null) return undefined
    return buildDensity({ start: globalStartMs, end: globalEndMs }, itemTimestamps)
  }, [globalStartMs, globalEndMs, itemTimestamps])

  // Count of items whose timestamp falls inside the current viewport — drives
  // the "X / Y events" readout in the bottom filter strip.
  const visibleEventCount = useMemo(() => {
    if (!viewport) return items.length
    let n = 0
    for (const t of itemTimestamps) {
      if (t >= viewport.start && t <= viewport.end) n += 1
    }
    return n
  }, [viewport, itemTimestamps, items.length])

  const { selection, select, clearSelection } = usePureTimelineStore()

  // Hover popup state — updates with the currently hovered card part.
  const [hover, setHover] = useState<{
    selection: SelectedNode | SelectedEdge
    anchor: HTMLElement
  } | null>(null)
  const hoverAnchorRef = useRef<HTMLElement | null>(null)
  hoverAnchorRef.current = hover?.anchor ?? null

  useEffect(() => {
    return () => clearSelection()
  }, [caseId, clearSelection])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') clearSelection()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [clearSelection])

  // Mirror node selection into the canonical selection-store so the
  // InvestigationSidebar's matching entity row auto-expands and scrolls into
  // view (sidebar's inline entity-detail watches this store).
  useEffect(() => {
    const replace = useSelectionStore.getState().replace
    if (selection?.kind === 'node') {
      replace([selection.id], 'timeline')
    } else if (!selection) {
      replace([], 'timeline')
    }
  }, [selection])

  const handleSelectEdge = useCallback(
    (timelineItemId: string) => {
      if (useSnippetSelection.getState().select(caseId, timelineItemId, 'timeline')) {
        select({ kind: 'edge', id: timelineItemId })
        return
      }
      const item = itemMap.get(timelineItemId)
      if (!item) return
      // Node-sourced events also flow through onSelectEdge from the card
      // outer button — route them to node selection so the entity-detail
      // panel opens with the right id.
      if (item.source_kind === 'node') {
        const primary = item.participants.primary
        const targetId = primary.id ?? item.source_id
        select({ kind: 'node', id: targetId }, { node: participantToSelectedNode(primary) })
        return
      }
      const edge = itemToSelectedEdge(item)
      if (!edge) return
      // Keep the selection key as the timeline-item (deterministic) id so
      // the canvas highlights the right card; the SelectedEdge payload
      // carries the underlying Neo4j element id for downstream consumers.
      select({ kind: 'edge', id: timelineItemId }, { edge })
    },
    [caseId, itemMap, select]
  )

  const handleSelectNode = useCallback(
    (nodeId: string) => {
      if (useSnippetSelection.getState().select(caseId, nodeId, 'timeline')) return
      const part = participantMap.get(nodeId)
      select(
        { kind: 'node', id: nodeId },
        {
          node: part
            ? participantToSelectedNode(part)
            : { id: nodeId, name: nodeId, labels: [], properties: {} },
        }
      )
    },
    [caseId, participantMap, select]
  )

  const handleHoverEnter = useCallback(
    (payload: TimelineHoverPayload) => {
      // A tap fires the hover too. On a phone it also opens the sidebar over
      // the canvas, and the preview would be left floating on top of it.
      if (isMobileViewport()) return
      if (snippetSelection(payload.nodeId ?? payload.edgeId ?? '')) return
      if (payload.kind === 'edge' && payload.edgeId) {
        const item = itemMap.get(payload.edgeId)
        if (!item) return
        const edge = itemToSelectedEdge(item)
        if (!edge) return
        setHover({ selection: edge, anchor: payload.anchor })
      } else if (payload.kind === 'node' && payload.nodeId) {
        const part = participantMap.get(payload.nodeId)
        if (!part) return
        setHover({ selection: participantToSelectedNode(part), anchor: payload.anchor })
      }
    },
    [itemMap, participantMap]
  )

  const handleHoverLeave = useCallback(() => {
    setHover(null)
  }, [])

  const eventCount = items.length
  const isEmpty = !isLoading && eventCount === 0
  // Events can arrive that no axis can hold: every one of their timestamps
  // failed to parse, so `globalRange` stays null and no viewport is ever
  // seeded. That state used to fall through every branch below and render
  // literally nothing — blank canvas, no overlay, no error, while the header
  // still counted the events. Name it so it reaches the user as a message.
  const isUnplottable = eventCount > 0 && globalRange === null

  const preparing = (
    <div className="absolute inset-0 flex items-center justify-center gap-2 text-muted-foreground">
      <Loader2 className="size-4 animate-spin" />
      <span className="text-xs">{t('timeline.loadingEvents')}</span>
    </div>
  )

  const overlay = (() => {
    if (isError)
      return { label: 'Channel lost', detail: 'Graph fetch failed — reload the view to retry.' }
    if (isUnplottable)
      return {
        label: 'Unreadable timestamps',
        detail: `${eventCount} event${eventCount === 1 ? '' : 's'} carry dates the axis can't read — check their date properties in the graph.`,
      }
    if (activeGraphFilters.length > 0)
      return {
        label: 'Filtered out',
        detail: 'No events match the active filters — remove a filter to restore signal.',
      }
    return {
      label: 'No temporal signal',
      detail: 'Add time-stamped data to the graph to populate the axis.',
    }
  })()

  return (
    <div className="relative flex h-full w-full flex-col bg-background text-foreground">
      <ObjectSetChipsBar view="timeline" />
      <div className="flex h-10 shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-border bg-background/80 px-3">
        <span className="text-xs font-medium text-muted-foreground">{t('timeline.label')}</span>
        <span className="h-4 w-px bg-border" />
        <span className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">{t('timeline.events')}</span>
          <span className="font-mono tabular-nums text-foreground">
            {eventCount.toString().padStart(3, '0')}
          </span>
        </span>
        {hasBypassedFilter ? (
          <span className="rounded-sm border border-primary/40 bg-primary/10 px-2 py-0.5 text-3xs uppercase tracking-wider text-primary">
            {t('timeline.complexFilterBypassed')}
          </span>
        ) : null}
        <span className="ml-auto hidden text-xs text-muted-foreground/80 md:inline">
          {t('timeline.hints')}
        </span>
      </div>

      <div className="relative flex-1 min-h-0 overflow-hidden">
        {objectSet.ids === null ? (
          <div className="absolute inset-0 flex items-center justify-center bg-background">
            <Card variant="floating" className="w-full max-w-5xl p-0">
              <EmptyStateInsightsPrompt
                view="timeline"
                {...(emptyStateProps ?? { onAskAgent: () => agent.setOpen(true) })}
              />
            </Card>
          </div>
        ) : isLoading ? (
          preparing
        ) : isEmpty || isError || isUnplottable ? (
          <SignalLostOverlay label={overlay.label} detail={overlay.detail} />
        ) : viewport ? (
          <TimelineCanvas
            events={cardEvents}
            viewport={viewport}
            selectedEdgeId={selection?.kind === 'edge' ? selection.id : null}
            onSelectEdge={handleSelectEdge}
            onSelectNode={handleSelectNode}
            onZoomAtCursor={zoomAtCursor}
            onPanBy={panBy}
            onFitAll={fitAll}
            onHoverEnter={handleHoverEnter}
            onHoverLeave={handleHoverLeave}
          />
        ) : (
          // Plottable events with no viewport yet — the seeding effect in
          // `useTimelineViewport` runs after this render. Every branch returns
          // something so the canvas can never silently go blank again; this
          // one is a single frame, so it shows the same spinner as the fetch
          // rather than an overlay that would flash a wrong diagnosis.
          preparing
        )}
        {sidebarSlot}
        {/* Top-right floating button stack — filter toggle + caller-provided
            accessory (e.g. SidebarOpenButton). Lives inside the canvas region
            so it clears the h-10 header bar and doesn't fight the "drag · wheel
            · dbl-click" hint that sits at the right of that header. The stack
            slides left when the InvestigationSidebar is open so the filter
            doesn't get covered. */}
        {objectSet.ids !== null && !isLoading && (
          <div
            className={cn(
              'pointer-events-none absolute top-3 z-30 flex flex-col gap-2',
              sidebarOpen && 'max-md:hidden'
            )}
            style={{ right: topRightOffsetPx }}
          >
            {/* Filter button — visual parity with CytoscapeGraph.tsx so the
                same control reads the same across views (per-class match,
                not just "looks similar"). */}
            <button
              type="button"
              onClick={() => setFilterOpen(prev => !prev)}
              className={cn(
                'pointer-events-auto p-2 rounded-lg transition-colors flex-shrink-0',
                filterOpen
                  ? 'bg-blue-600 text-white'
                  : 'bg-background border border-border text-muted-foreground hover:text-foreground hover:bg-accent'
              )}
              title="Filter by node type"
              aria-pressed={filterOpen}
            >
              <Filter className="size-5" />
            </button>
            {topRightAccessory ? (
              <div className="pointer-events-auto">{topRightAccessory}</div>
            ) : null}
          </div>
        )}
        <EntityTypeFilterPanel
          open={filterOpen}
          stats={entityTypeStats}
          hiddenCount={hiddenCount}
          onToggle={toggleEntityType}
          onShowAll={showAllEntityTypes}
          onClose={() => setFilterOpen(false)}
          rightOffset={topRightOffsetPx + 48}
          topOffset={12}
        />
      </div>

      {viewport && globalStartMs !== null && globalEndMs !== null ? (
        <TimeRangeFilter
          className={sidebarOpen ? 'max-md:hidden' : undefined}
          view="timeline"
          bounds={{ start: globalStartMs, end: globalEndMs }}
          value={{ start: viewport.start, end: viewport.end }}
          onChange={range => {
            if (range)
              setViewport({
                start: range.start,
                end: range.end,
              })
            else fitAll()
          }}
          density={timelineDensity}
          timestamps={itemTimestamps}
          total={items.length}
          visibleCount={visibleEventCount}
          onAutoFit={fitAll}
          countSuffix="events"
        />
      ) : null}

      {hover ? (
        <EntityHoverPopup
          selection={hover.selection}
          open
          onOpenChange={next => {
            if (!next) setHover(null)
          }}
          anchor={{ type: 'ref', ref: hoverAnchorRef as React.RefObject<HTMLElement> }}
        />
      ) : null}
    </div>
  )
}
